import { DatasetDetails } from '@interfaces/dataset';
import { AppConfigAPI } from '@models/types/appconfig.d';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ImportDataSet } from '@models/import-data-set';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { NotificationsService } from '../notifications/notifications.service';
import { RequiredDatasetImportResponse } from '@models/required-dataset-import-response';
import { shareReplay, tap, map, defaultIfEmpty } from 'rxjs/operators';
import { ImportSetting } from '@models/import-settings';

// TODO: Replace when we implement Importer Service
const mockDataset = {
  DataSetName: 'ALM Balance Sheet - August 30 2018.ALM/XLSX',
  DataSetImportDate: '6/8/2018 8:20:20 AM',
};

@Injectable({ providedIn: 'root' })
export class DatasetsService {
  private environment: AppConfigAPI;
  private _isImportRequired = false;
  private datasetApiUrl;
  private cpURL;
  private calApiUrl;
  private initiateImport;
  private initiateAggregation;
  private pollFileImport;
  private pollAggregationImport;
  private initiateReAggregation;
  private getImportSettings;
  private _currentDataset;

  public importSettings$ = new BehaviorSubject(null);

  public get isImportRequired(): boolean {
    return this._isImportRequired;
  }

  public get currentDataset() {
    return this._currentDataset;
  }
  public set currentDataset(value) {
    this._currentDataset = value;
  }

  constructor(private http: HttpClient, private notifications: NotificationsService, private appConfig: AppConfigProvider) {
    if (!this.currentDataset) {
      this.currentDataset = mockDataset;
    }
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;

      this.datasetApiUrl = `${this.environment.DATASETAPI}/api/dataset`;
      this.cpURL = `${this.environment.DATASETAPI}/api/dataset/cp`;
      this.calApiUrl = `${this.environment.CALCAPI}/api/param/CheckDataImport`;
      this.initiateImport = `${this.environment.DATASETAPI}/api/dataset/Import`;
      this.initiateAggregation = `${this.environment.DATASETAPI}/api/dataset/Aggregate`;
      this.pollFileImport = `${this.environment.DATASETAPI}/api/dataset/PollFileImport`;
      this.pollAggregationImport = `${this.environment.DATASETAPI}/api/dataset/PollAggregationImport`;
      this.initiateReAggregation = `${this.environment.DATASETAPI}/api/dataset/ReAggregate`;
      this.getImportSettings = `${this.environment.DATASETAPI}/api/dataset/import_settings`;
    });
  }

  public InititateFileImport(file, json): Observable<any> {
    const dataset: ImportDataSet = new ImportDataSet();

    dataset.UserID = -1;
    dataset.FileName = file;
    dataset.Headers = json;
    dataset.ImportID = 0;
    dataset.DataSets = null;
    // console.log('Dataset', dataset);
    const datasets$ = this.http.post(this.initiateImport, dataset);

    return datasets$;
  }

  public GetImportSettings() {
    return this.http.get<ImportSetting>(this.getImportSettings).pipe(
      // Make the last value available to be consumed without making new requests
      tap(settings => this.importSettings$.next(settings)),
      shareReplay(1)
    );
  }

  public PollImport(importId, file, type: number): any {
    const dataset: ImportDataSet = new ImportDataSet();

    dataset.UserID = -1;
    dataset.FileName = file;
    dataset.Headers = null;
    dataset.ImportID = importId;
    dataset.DataSets = null;
    if (type === 0) {
      return this.http.post(this.pollFileImport, dataset);
    } else {
      return this.http.post(this.pollAggregationImport, dataset);
    }
  }

  public InititateAggregation(importID: number, datasets: string[]): Observable<any> {
    const dataset: ImportDataSet = new ImportDataSet();

    dataset.UserID = -1;
    dataset.FileName = '';
    dataset.Headers = null;
    dataset.ImportID = importID;
    dataset.DataSets = datasets;
    const datasets$ = this.http.post(this.initiateAggregation, dataset);
    return datasets$;
  }

  public startReAggregation(type: number) {
    const dataset: ImportDataSet = new ImportDataSet();
    dataset.UserID = -1;
    dataset.FileName = '';
    dataset.Headers = null;
    dataset.ImportID = -1;
    dataset.DataSets = null;
    dataset.Is_ALM_Import = type === 1 ? false : true;

    //console.log('Dataset ', dataset);
    const datasets$ = this.http.post(this.initiateReAggregation, dataset);
    return datasets$;
  }

  public getDatasets(): Observable<DatasetDetails[]> {
    const mapImportDateToUTCValue = (dataset: DatasetDetails) => ({
      ...dataset,
      DataSetImportDate: `${dataset.DataSetImportDate} UTC`,
    });

    const datasets$ = this.http.get<DatasetDetails[]>(this.datasetApiUrl).pipe(
      map((datasetArray: DatasetDetails[]) => datasetArray.map(mapImportDateToUTCValue)),
      defaultIfEmpty([])
    );
    return datasets$;
  }

  public getCurrentPosition(): Observable<any> {
    const datasets$ = this.http.get(this.cpURL) || of([]);
    return datasets$;
  }

  public async getRequiredDatasetImport(): Promise<boolean> {
    return this.http
      .get(this.calApiUrl)
      .toPromise()
      .then((res: RequiredDatasetImportResponse[]) => {
        this.notifications.deleteAllNotificationsByType('dataImportRequired');
        if (res[0] && res[0].ParamValue && res[0].ParamValue === 'Y') {
          this.notifications.addNewNotification(
            'dataImportRequired',
            'New Source Data Required',
            'There are changes that require a data reimport, please import your Source Data again.',
            'this will contain whatever information we need to pass to the url that will initiate a data import'
          );

          this._isImportRequired = true;
        } else {
          this._isImportRequired = false;
        }
        return this.isImportRequired;
      })
      .catch(error => {
        return Promise.reject(error);
      });
  }

  public uploadFile(file, filename) {
    if (file) {
      const fileUploadForm: FormData = new FormData();
      fileUploadForm.append('uploadedDataset', file, filename);
      return this.http.post(`${this.datasetApiUrl}/ImportLocal/`, fileUploadForm);
    }
  }
}
